'use client';

import { FaChrome } from 'react-icons/fa6';
import report, { ReportEvent } from '@/lib/report';

const CHROME_STORE_URL =
  'https://chromewebstore.google.com/detail/pengi-live-tutor/lkjhjffmaaiaikckielejcaefdpbdhkg';

export interface AddToChromeButtonProps {
  /** Position identifier for analytics (e.g. 'hero', 'bottom') */
  position: string;
  /** Landing page slug for analytics (e.g. 'phet') */
  page: string;
  /** Tailwind classes for the outer link */
  className?: string;
  /** Button label */
  label?: string;
  /** Icon size in pixels */
  iconClassName?: string;
  /** Enable hover rotate on chrome icon */
  animateIcon?: boolean;
}

/**
 * Shared "Add to Chrome" CTA button for the PhET landing page.
 *
 * Fires `extension_install_initiated` on click so we can measure the
 * ad-click → extension-install funnel. The Chrome Web Store navigation
 * happens in a new tab (`target="_blank"`) so PostHog has time to flush
 * the event before the user leaves.
 */
export default function AddToChromeButton({
  position,
  page,
  className,
  label = 'Add to Chrome',
  iconClassName = 'h-5 w-5',
  animateIcon = true,
}: AddToChromeButtonProps) {
  const handleClick = () => {
    // Fire PostHog event for click-to-install tracking. The link opens in
    // a new tab so this fires synchronously before the user navigates.
    report(ReportEvent.EXTENSION_INSTALL_INITIATED, {
      page,
      position,
      // Capture UTM params at click time so we can attribute installs
      // to the originating ad/channel even though the install itself
      // happens off-site.
      utm_source:
        typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search).get('utm_source')
          : null,
      utm_medium:
        typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search).get('utm_medium')
          : null,
      utm_campaign:
        typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search).get('utm_campaign')
          : null,
      utm_content:
        typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search).get('utm_content')
          : null,
      referrer: typeof document !== 'undefined' ? document.referrer : null,
    });

    // Google Ads conversion event (layout.tsx has a global listener, but we
    // also fire here so the `position` field is attached to the GA4 event).
    if (typeof window !== 'undefined' && (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag) {
      (window as unknown as { gtag: (...args: unknown[]) => void }).gtag('event', 'add_to_chrome', {
        page,
        position,
      });
    }
  };

  return (
    <a
      href={CHROME_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      data-ph-capture-attribute-button={`add-to-chrome-${position}`}
      data-ph-capture-attribute-page={page}
      className={className}
    >
      <FaChrome
        className={`${iconClassName} ${animateIcon ? 'transition-transform duration-300 group-hover:rotate-180' : ''}`}
      />
      {label}
    </a>
  );
}
